import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, ArrowRight, ClipboardList, Sparkles, Stethoscope } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { RelatedReading } from "@/components/RelatedReading";
import { References } from "@/components/References";
import { ClusterLinks } from "@/components/ClusterLinks";
import { AuthorityFlow } from "@/components/AuthorityFlow";
import { relatedArticles } from "@/lib/clusters";
import { ClinicalProductPitch } from "@/components/ClinicalProductPitch";
import heroImage from "@/assets/blog/late-period.webp";
import stressImage from "@/assets/blog/late-period-stress.webp";
import metabolicImage from "@/assets/blog/late-period-metabolic.webp";


const URL = "https://truwe.in/blog/why-is-my-period-late";
const TITLE = "Why Is My Period Late? Causes, Tests & When to See a Doctor | TruWe";

const FAQS = [
  {
    q: "My period is 3 days late. Should I worry?",
    a: "Usually not. A few days of variation can happen naturally. Consider a pregnancy test if pregnancy is possible, particularly once you have reached the first day of your missed period. Look at the pattern rather than one isolated cycle.",
  },
  {
    q: "My period is 7 days late. Does that mean I am pregnant?",
    a: "No. Pregnancy is one possible cause, but stress, PCOS, weight changes, thyroid problems, illness, heavy exercise and contraception can also delay periods. If pregnancy is possible, take a home pregnancy test rather than relying on symptoms alone.",
  },
  {
    q: "My period is late but my pregnancy test is negative. Why?",
    a: "Possible explanations include testing too early, delayed ovulation, stress, PCOS, thyroid problems, recent weight changes, heavy exercise, illness or contraception. If pregnancy is still possible, repeat the test after a few days. If tests remain negative and irregularity continues, speak with a healthcare professional.",
  },
  {
    q: "Can stress really delay a period?",
    a: "Yes. Significant stress can alter the hormonal signalling involved in ovulation. When ovulation is delayed, the following period is usually delayed as well.",
  },
  {
    q: "Can PCOS cause periods to be late every month?",
    a: "Yes. Irregular or absent ovulation is one of the defining features commonly associated with PCOS. Some women with PCOS have periods occasionally, while others may go several months without one. Repeated long cycles deserve medical assessment.",
  },
  {
    q: "Can you have PCOS without being overweight?",
    a: "Yes. PCOS can occur across different body sizes. Weight can affect metabolic risk, but being slim does not rule out PCOS.",
  },
  {
    q: "Can travelling make your period late?",
    a: "Travel may contribute indirectly through changes in sleep, stress, eating patterns, exercise and routine, which can sometimes affect ovulation timing.",
  },
  {
    q: "How can I make my period come immediately?",
    a: "There is no universal safe method to force a period without knowing why it is late. A missed period caused by pregnancy needs a completely different approach from one caused by PCOS, thyroid disease, energy deficiency or contraception. Identify the cause first.",
  },
  {
    q: "Do supplements make periods regular?",
    a: "No supplement can guarantee regular periods for every woman. Whether nutritional support is useful depends on the underlying reason for the irregularity. Persistent irregular periods should be medically evaluated first.",
  },
  {
    q: "How late is too late for a period?",
    a: "There is no single number for everyone. For most adults, cycles commonly range from about 21 to 35 days. A single unusual cycle may not indicate a problem, but repeatedly going beyond your usual pattern, or regularly having cycles longer than about 35 days, deserves attention.",
  },
] as const;

export const Route = createFileRoute("/blog/why-is-my-period-late")({
  component: Article,
  head: () =>
    buildBlogHead({
      slug: "why-is-my-period-late",
      title: TITLE,
      description:
        "Why periods get delayed: pregnancy, stress, PCOS, thyroid problems, weight changes, over-exercise, contraception, breastfeeding and perimenopause, plus when to see a doctor.",
      ogTitle: "Why Is My Period Late?",
      ogDescription:
        "A late period is a signal, not a diagnosis. The most common reasons your period is delayed, and when to investigate.",
      headline: "Why Is My Period Late?",
      breadcrumbName: "Why Is My Period Late?",
      datePublished: "2026-08-24",
      dateModified: "2026-08-24",
      ogImage: `https://truwe.in${heroImage}`,
      faqs: FAQS.map((f) => ({ q: f.q, a: f.a })),
    }),
});

const QUICK_CAUSES = [
  "Pregnancy",
  "Stress",
  "PCOS",
  "Thyroid problems",
  "Rapid weight loss or weight gain",
  "Eating too little for your body's energy needs",
  "Heavy or excessive exercise",
  "Hormonal contraception",
  "Breastfeeding",
  "Perimenopause",
  "Certain illnesses or medications",
] as const;

const SEE_DOCTOR = [
  "You have missed three periods in a row",
  "Your cycles repeatedly extend beyond roughly 35 days",
  "Your periods have suddenly become significantly irregular",
  "You have not had your first period by age 15",
  "You have irregular periods alongside unexplained weight changes",
  "You develop excess facial or body hair, or significant acne",
  "You have persistent fatigue or other thyroid-like symptoms",
  "You are having difficulty becoming pregnant",
  "You experience nipple discharge when not breastfeeding",
  "You have very heavy or unusual bleeding",
] as const;

const TESTS = [
  "Pregnancy testing",
  "Thyroid function tests",
  "Prolactin",
  "Androgen or testosterone levels",
  "Glucose or metabolic markers",
  "Other reproductive hormones",
  "Pelvic ultrasound when appropriate",
] as const;

const SOURCES = [
  {
    label:
      "American College of Obstetricians and Gynecologists — guidance on menstrual cycles and amenorrhea",
    url: "https://www.acog.org/womens-health/faqs/your-first-period",
  },
  {
    label: "NHS — missed or late periods",
    url: "https://www.nhs.uk/conditions/stopped-or-missed-periods/",
  },
  {
    label: "NHS — irregular periods",
    url: "https://www.nhs.uk/conditions/irregular-periods/",
  },
  {
    label: "World Health Organization — Polycystic ovary syndrome fact sheet",
    url: "https://www.who.int/news-room/fact-sheets/detail/polycystic-ovary-syndrome",
  },
  {
    label:
      "International Evidence-Based Guideline for the Assessment and Management of PCOS (2023)",
    url: "https://www.asrm.org/practice-guidance/practice-committee-documents/international-evidence-based-guideline-for-the-assessment-and-management-of-polycystic-ovary-syndrome-pcos/",
  },
  {
    label:
      "Bharali et al. — prevalence of PCOS in India: a systematic review and meta-analysis",
    url: "https://pubmed.ncbi.nlm.nih.gov/36461447/",
  },
] as const;

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
      {children}
    </h2>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="mt-3 text-base leading-relaxed text-foreground/85">{children}</p>;
}

function Bullets({ items }: { items: readonly string[] }) {
  return (
    <ul className="mt-4 space-y-2 text-base text-foreground/85">
      {items.map((i) => (
        <li key={i} className="flex gap-2.5">
          <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
          <span className="min-w-0">{i}</span>
        </li>
      ))}
    </ul>
  );
}

function Article() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            Clinical Library · Hormonal Health
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            Why is my period late?
          </h1>
          <BlogByline date="2026-08-24" readingTime="12 min read" />
          <div className="mt-4">
            <ReadWithLlm title={TITLE} url={URL} kind="article" />
          </div>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            Understanding the most common reasons your period is delayed, from pregnancy
            and stress to PCOS, thyroid issues and metabolic health.
          </p>
          <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={heroImage}
              alt="A monthly calendar with a circled date beside a clock and a cup of herbal tea"
              width={1200}
              height={630}
              loading="eager"
              className="h-auto w-full"
            />
          </figure>
        </Reveal>

        <Reveal delay={0.05}>
          <P>
            If your period has not arrived when you expected it, the first thing to know
            is that a late period does not automatically mean something is wrong.
          </P>
          <P>
            Pregnancy is one possible reason, but periods can also be delayed by stress,
            PCOS, thyroid problems, changes in body weight, intense exercise, illness,
            hormonal contraception, breastfeeding and perimenopause.
          </P>
          <P>
            For most adults, menstrual cycles commonly fall between 21 and 35 days,
            although what is normal for you matters too. An occasional longer cycle can
            happen. Repeatedly long, unpredictable or absent periods deserve more
            attention.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-10 rounded-3xl border border-brand/20 bg-brand/[0.04] p-6 md:p-8">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand">
              <ClipboardList className="h-4 w-4" /> Quick answer
            </div>
            <h2 className="mt-2 font-display text-xl font-bold text-brand-deep md:text-2xl">
              Why can a period be late?
            </h2>
            <Bullets items={QUICK_CAUSES} />
            <p className="mt-4 text-sm text-foreground/80">
              The important question is not simply “How do I make my period come?” It is
              “Why was ovulation or menstruation delayed in the first place?” Understanding
              the cause matters more than trying to force a period.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>1. Pregnancy</H2>
          <P>
            If you are sexually active, pregnancy is usually the first possibility to rule
            out. Even when contraception is used correctly, no method is completely
            effective.
          </P>
          <P>
            Most home pregnancy tests can be used from the first day of a missed period. If
            you do not know when your period was due, NHS guidance recommends testing at
            least 21 days after the last episode of unprotected sex. If the test is
            negative but your period still does not arrive, repeating it after a few days
            may be appropriate.
          </P>
          <div className="mt-6 rounded-2xl border border-destructive/25 bg-destructive/[0.05] p-5">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-destructive">
              <AlertTriangle className="h-4 w-4" /> Important
            </div>
            <p className="mt-2 text-sm text-foreground/85">
              A missed period combined with severe or persistent pain on one side of the
              lower abdomen, unusual vaginal bleeding, shoulder-tip pain, severe dizziness
              or fainting requires urgent medical assessment if pregnancy is possible.
              These can be warning signs of an ectopic pregnancy.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>2. Stress</H2>
          <P>
            Yes, stress can delay a period. Your menstrual cycle depends on communication
            between your brain, pituitary gland and ovaries, often called the
            hypothalamic-pituitary-ovarian axis. Significant physical or psychological
            stress can disturb the hormonal signalling involved in ovulation. If ovulation
            happens later than usual, your period usually arrives later as well.
          </P>
          <P>Stress-related changes may occur during periods of:</P>
          <Bullets
            items={[
              "Major emotional stress, grief or relationship problems",
              "Examinations or demanding work schedules",
              "Prolonged poor sleep",
              "Travel and significant routine disruption",
              "Illness",
            ]}
          />
          <P>
            This does not mean every stressful day will alter your cycle. But sustained or
            significant stress is a recognised cause of late periods.
          </P>
          <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={stressImage}
              alt="Calm morning routine with a journal and herbal tea, representing stress recovery and cycle regularity"
              width={1200}
              height={630}
              loading="lazy"
              decoding="async"
              className="h-auto w-full"
            />
            <figcaption className="bg-card px-4 py-3 text-xs text-muted-foreground">
              Sleep, recovery and lower sustained stress support the brain-ovary signalling
              that sets your cycle length.
            </figcaption>
          </figure>
        </Reveal>


        <Reveal delay={0.05}>
          <H2>3. PCOS</H2>
          <P>
            If late periods happen repeatedly, polycystic ovary syndrome (PCOS) becomes an
            important possibility to investigate. PCOS is a hormonal and metabolic
            condition that can interfere with normal ovulation. When ovulation does not
            happen regularly, periods may become late, unpredictable, more than 35 days
            apart, very infrequent, or absent for months.
          </P>
          <P>
            WHO estimates that PCOS affects around 10–13% of women of reproductive age
            globally, and that many affected women remain undiagnosed. An Indian systematic
            review and meta-analysis estimated a pooled prevalence of approximately 11.3%,
            although estimates vary substantially depending on the diagnostic criteria
            used.
          </P>
          <h3 className="mt-8 font-display text-xl font-bold text-brand-deep">
            Other possible signs of PCOS
          </h3>
          <Bullets
            items={[
              "Acne or oily skin",
              "Increased facial or body hair",
              "Scalp hair thinning",
              "Difficulty conceiving",
              "Weight gain, particularly around the abdomen",
              "Difficulty managing weight",
            ]}
          />
          <P>
            You do not need to have every symptom to have PCOS. And despite the name,
            ovarian “cysts” are not required for a diagnosis. WHO describes diagnosis as a
            combination of irregular ovulation, signs of elevated androgens and/or
            polycystic ovarian appearance, after other causes have been excluded.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>What does insulin resistance have to do with PCOS?</H2>
          <P>
            PCOS should not be viewed only as a problem with periods. For many women it
            also has an important metabolic component. Insulin is the hormone that helps
            move glucose from the bloodstream into cells. When the body becomes less
            responsive to insulin, it may compensate by producing more of it. Insulin
            resistance is commonly associated with PCOS and can interact with reproductive
            hormones and androgen production.
          </P>
          <P>
            This is one reason modern PCOS management looks beyond “How can I get my
            period?” and also considers metabolic health, insulin sensitivity, nutrition,
            physical activity, sleep, body composition, and long-term cardiovascular and
            diabetes risk. A late period can sometimes be the visible symptom of a much
            broader hormonal-metabolic pattern.
          </P>
          <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={metabolicImage}
              alt="Balanced plate of leafy greens, nuts and berries with a walking shoe, representing insulin-sensitive eating and movement"
              width={1200}
              height={630}
              loading="lazy"
              decoding="async"
              className="h-auto w-full"
            />
            <figcaption className="bg-card px-4 py-3 text-xs text-muted-foreground">
              Protein-and-fibre-first meals, daily movement and steady sleep are the levers
              most often used to improve insulin sensitivity in PCOS.
            </figcaption>
          </figure>
        </Reveal>


        <Reveal delay={0.05}>
          <H2>4. Thyroid problems</H2>
          <P>
            Your thyroid may seem unrelated to your cycle, but thyroid hormones interact
            with reproductive hormone signalling. Both an underactive thyroid
            (hypothyroidism) and an overactive thyroid (hyperthyroidism) can affect
            menstrual regularity.
          </P>
          <Bullets
            items={[
              "Unusual tiredness",
              "Unexplained weight changes",
              "Feeling unusually cold or hot",
              "Changes in heart rate",
              "Bowel changes",
              "Changes in hair or skin",
            ]}
          />
          <P>
            A blood test can help determine whether thyroid function may be contributing.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>5. Rapid weight loss or eating too little</H2>
          <P>
            Your reproductive system requires sufficient energy availability. Rapid weight
            loss, very low calorie intake, being significantly underweight or maintaining
            very low body fat can interfere with the hormonal signals required for
            ovulation.
          </P>
          <P>
            In some cases this develops into functional hypothalamic amenorrhea, where
            periods become very irregular or stop because reproductive signalling has been
            suppressed. This can occur even in someone who appears otherwise fit and
            healthy. The combination of high exercise, inadequate calorie intake and
            physiological stress is particularly relevant.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>6. Weight gain and metabolic changes</H2>
          <P>
            Weight gain can also affect menstrual regularity. Changes in body composition
            can influence insulin sensitivity, estrogen metabolism, androgen signalling and
            ovulation.
          </P>
          <P>
            Weight alone does not diagnose a hormonal condition. Plenty of women with
            higher body weight have completely regular cycles, and many women with PCOS are
            not overweight. But when irregular cycles occur alongside abdominal weight
            gain, acne or increased facial hair, evaluation for PCOS or another endocrine
            cause may be appropriate.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>7. Too much exercise</H2>
          <P>
            Exercise is generally beneficial for health. However, very high training loads
            without enough nutrition and recovery can suppress reproductive hormone
            signalling. This is more commonly seen in endurance athletes, dancers, and
            people training intensely while dieting or rapidly trying to lose body fat.
          </P>
          <P>
            The problem is not exercise itself. It is often the combination of excessive
            physical demand and insufficient energy availability.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>8. Hormonal birth control</H2>
          <P>
            Starting, stopping or changing hormonal contraception can change your bleeding
            pattern. This can occur with contraceptive pills, hormonal IUDs, contraceptive
            injections and implants. Some methods may make periods lighter, irregular or
            absent altogether, which does not necessarily mean that something is wrong. If
            your bleeding pattern changes significantly, discuss it with your healthcare
            provider.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>9. Breastfeeding</H2>
          <P>
            Breastfeeding increases levels of the hormone prolactin. Higher prolactin can
            suppress ovulation, meaning periods may remain absent or irregular for some
            time after childbirth. The timing of period return varies considerably between
            individuals. Importantly, ovulation can return before the first postpartum
            period, so absence of periods should not be considered reliable contraception.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>10. Perimenopause</H2>
          <P>
            Periods naturally become less predictable as menopause approaches. This
            transitional phase is called perimenopause and often begins during the 40s,
            although timing varies. Cycles can become shorter, longer, heavier, lighter or
            occasionally skipped. Other symptoms can include hot flashes, sleep disturbance
            and changes in mood.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>Is it normal for a period to be a few days late?</H2>
          <P>
            Often, yes. The menstrual cycle is not a machine programmed to run on exactly
            the same number of days every month. For most adult women, cycles between
            approximately 21 and 35 days are generally considered within the typical range.
          </P>
          <P>
            What matters is your pattern. If your cycles are normally 27–29 days and
            suddenly become 40–45 days repeatedly, that change deserves more attention than
            a single period arriving three days late.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>When should you see a doctor about a late period?</H2>
          <div className="mt-4 rounded-3xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand">
              <Stethoscope className="h-4 w-4" /> Arrange a medical evaluation if
            </div>
            <Bullets items={SEE_DOCTOR} />
            <p className="mt-4 text-sm text-muted-foreground">
              NHS guidance specifically recommends medical assessment after three
              consecutive missed periods, or when missed periods occur alongside symptoms
              such as weight changes, tiredness, facial hair growth or skin changes.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>What tests might a doctor do for irregular periods?</H2>
          <P>
            The exact investigation depends on your symptoms and medical history. A
            healthcare professional may consider:
          </P>
          <Bullets items={TESTS} />
          <P>
            Not every woman needs every test. The objective is to determine why ovulation
            or menstruation has changed, rather than simply treating the late period
            itself.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>Can lifestyle changes help make periods more regular?</H2>
          <P>
            They can help when lifestyle or metabolic factors are contributing to irregular
            ovulation. Useful foundations include:
          </P>
          <div className="mt-5 space-y-4">
            {[
              {
                title: "Sleep",
                note: "Consistent sleep supports normal metabolic and hormonal regulation.",
              },
              {
                title: "Adequate nutrition",
                note: "Avoid extreme calorie restriction and rapid weight-loss cycles.",
              },
              {
                title: "Regular physical activity",
                note: "Exercise helps, but chronic overtraining without adequate recovery can work in the opposite direction.",
              },
              {
                title: "Stress management",
                note: "Reducing prolonged psychological and physiological stress may help restore more stable reproductive signalling when stress is part of the cause.",
              },
              {
                title: "Metabolic health",
                note: "For women with PCOS or insulin resistance, improving metabolic health can be an important part of long-term management.",
              },
            ].map((row) => (
              <div key={row.title} className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-display text-lg font-semibold text-brand-deep">
                  {row.title}
                </h3>
                <p className="mt-1.5 text-sm text-foreground/80">{row.note}</p>
              </div>
            ))}
          </div>
          <P>
            The 2023 international evidence-based PCOS guideline emphasises healthy
            lifestyle behaviours as a central component of PCOS management.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>Where do supplements fit?</H2>
          <P>
            Supplements should not be used to avoid investigating persistent menstrual
            irregularity. The first priority is understanding the cause.
          </P>
          <P>
            For example, nutritional approaches such as inositol are frequently discussed
            in relation to PCOS and metabolic health. Current international PCOS guidance
            states that inositol may be considered according to individual preferences,
            with potential metabolic benefits but limited evidence for major clinical
            outcomes such as ovulation or weight. The guideline also states that there is
            currently insufficient evidence to recommend one specific inositol type or dose
            for everyone with PCOS.
          </P>
          <P>
            That distinction matters. Nutrition can support a broader health protocol. It
            should not be presented as a substitute for diagnosis or appropriate medical
            treatment.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-14 rounded-3xl border border-brand/15 bg-gradient-to-br from-brand/5 via-background to-brand-deep/5 p-8 md:p-10">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand">
              <Sparkles className="h-4 w-4" /> The TruWe formulation
            </div>
            <h2 className="mt-3 font-display text-2xl font-bold text-brand-deep md:text-3xl">
              Ova Balance, for cycles driven by insulin resistance
            </h2>
            <p className="mt-3 text-base text-foreground/85">
              If your evaluation points to PCOS with a metabolic component, Ova Balance
              combines <strong>40:1 myo:D-chiro-inositol, berberine HCl and Quatrefolic®
              L-5-MTHF</strong> at research-backed doses. It supports a medical plan, it
              does not replace one.
            </p>
            <Link
              to="/products/$handle"
              params={{ handle: "ova-balance" }}
              className="mt-6 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-brand px-6 text-sm font-semibold text-white transition hover:bg-brand-deep"
            >
              Ova Balance — 40:1 inositol for PCOS <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>Frequently asked questions about late periods</H2>
          <div className="mt-5 space-y-4">
            {FAQS.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-display text-base font-bold text-brand-deep md:text-lg">
                  {f.q}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>The bottom line</H2>
          <P>
            A late period is a signal, not a diagnosis. Sometimes the explanation is
            simple: stress, travel, illness or normal cycle variation. Sometimes it points
            toward something worth investigating, such as pregnancy, PCOS, thyroid
            dysfunction or inadequate energy intake.
          </P>
          <P>
            If it happens once, track it. If it keeps happening, investigate it. And if
            irregular periods occur alongside acne, excess facial hair, metabolic changes
            or difficulty conceiving, looking deeper into PCOS and metabolic health may be
            particularly useful.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <ClinicalProductPitch cluster="hormonal" />
          <ClusterLinks cluster="hormonal" />
          <AuthorityFlow cluster="hormonal" />
          <References slug="why-is-my-period-late" />
          <RelatedReading articles={relatedArticles("why-is-my-period-late")} />
        </Reveal>

        <Reveal delay={0.05}>
          <H2>Editorial sources</H2>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
            {SOURCES.map((s) => (
              <li key={s.url}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  className="underline hover:text-brand"
                >
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-12 text-xs text-muted-foreground">
            Medical disclaimer: this article is intended for educational purposes only and
            does not diagnose, treat, cure or prevent any medical condition. Menstrual
            irregularities can have many different causes. Consult a qualified healthcare
            professional for personalised diagnosis and treatment, particularly if symptoms
            are persistent, severe or pregnancy is possible.
          </p>
        </Reveal>
      </div>
    </article>
  );
}
